import React from "react";
import { CellContext } from "@tanstack/react-table";
import type { ViewTableFeatures } from "@design-system/ViewTable/core/table";
import { ViewTableCell } from "@design-system/ViewTable/ViewTableCell";
// oxlint-disable-next-line no-restricted-imports -- Legacy migration debt — do not copy into new code.
import { EllipsisWithTooltip } from "@design-system/EllipsisWithTooltip/EllipsisWithTooltip";
import { HStack } from "@design-system/HStack/HStack";
import { Tag } from "@design-system/Tag/Tag";
import { useIdCopy } from "@hooks/useIdCopy";
import { CounterpartyDirectoryViewRow } from "../counterpartyDirectoryViewTypes";
import styles from "../DirectoryTable.module.scss";

export const CounterpartyNameCell: React.FC<
    CellContext<ViewTableFeatures, CounterpartyDirectoryViewRow, string>
> = React.memo(({ row }) => {
    const { getCopyProps } = useIdCopy({ label: "Counterparty" });

    if (row.original.isAccountRow) {
        return (
            <ViewTableCell>
                <EllipsisWithTooltip<HTMLSpanElement>
                    fullText={row.original.name}
                    overflowDirection="vertical"
                >
                    {(ref) => (
                        <span className={styles.name} ref={ref}>
                            {row.original.name}
                        </span>
                    )}
                </EllipsisWithTooltip>
            </ViewTableCell>
        );
    }

    const isCounterpartyDisabled = Boolean(
        row.original.counterparty?.disabledAt,
    );

    return (
        <ViewTableCell>
            <HStack gap={8} align="center">
                <EllipsisWithTooltip<HTMLDivElement>
                    fullText={row.original.name}
                    overflowDirection="vertical"
                >
                    {(ref) => (
                        <div
                            className={styles.name}
                            ref={ref}
                            {...getCopyProps(row.original.id)}
                        >
                            {row.original.name}
                        </div>
                    )}
                </EllipsisWithTooltip>
                {isCounterpartyDisabled && (
                    <Tag color="neutral" size="sm">
                        Disabled
                    </Tag>
                )}
            </HStack>
        </ViewTableCell>
    );
});

CounterpartyNameCell.displayName = "CounterpartyNameCell";
